<template>
  <teleport to="body">
    <div :class="['gfr-dropdown-wrapper', wrapperClass]" :style="rect && contentStyle" @click.stop="triggerClose">
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="transform scale-90 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-200 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-90 opacity-0"
        @before-enter="onOpen"
      >
        <div v-show="open" ref="ctx" :class="['gfr-dropdown-content', props.class]">
          <i v-if="props.type === 'arrow'" class="gfr-dropdown-content-arrow" :class="`${arrowPlacement}`" />
          <slot />
        </div>
      </transition>
    </div>
  </teleport>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import { isElementOutOfViewport } from '../utils'
import { UnknownProp } from '~/components/gfr/utils/base'
type placementOptions = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'bottom-center' | 'top-center'

const open = inject('open') as Ref<boolean>
const rect: any = inject('rect')
const triggerClose = inject('triggerClose') as () => void
const props = defineProps({
  class: UnknownProp,
  wrapperClass: UnknownProp,
  type: { type: String as PropType<'default' | 'arrow'>, default: 'default' },
  offsetX: { type: Number, default: 0 },
  offsetY: { type: Number, default: 12 },
  placement: { type: String as PropType<placementOptions>, default: 'bottom-start' }
})
const emit = defineEmits(['open'])
const ctx = ref<HTMLElement>()
const ctxRect = ref<{ width: number; height: number } | null>(null)
const arrowPlacement = ref(props.placement)

const contentStyle = computed(() => {
  if (!rect.value || !ctxRect.value) return ''
  const { height, top, left, right, width } = rect.value
  const { width: ctxWidth, height: ctxHeight } = ctxRect.value || { width: 0, height: 0 }
  const [startX, endX, topY, bottomY, centerX] = [
    left + props.offsetX,
    right - props.offsetX - ctxWidth,
    top - props.offsetY - ctxHeight,
    top + height + props.offsetY,
    left - ctxWidth / 2 + width / 2
  ]
  const variable = {
    '--dropdown-transform-x': '0px',
    '--dropdown-transform-y': '0px'
  }
  const actualPlacement = getActualPlacement(
    isElementOutOfViewport({
      x: props.placement.includes('end') ? endX : props.placement.includes('start') ? startX : centerX,
      y: props.placement.includes('top') ? topY : bottomY,
      width: ctxWidth,
      height: ctxHeight
    })
  )
  arrowPlacement.value = actualPlacement as placementOptions
  switch (actualPlacement) {
    case 'bottom-start':
      variable['--dropdown-transform-x'] = `${startX}px`
      variable['--dropdown-transform-y'] = `${bottomY}px`
      break
    case 'bottom-end':
      variable['--dropdown-transform-x'] = `${endX}px`
      variable['--dropdown-transform-y'] = `${bottomY}px`
      break
    case 'bottom-center':
      variable['--dropdown-transform-x'] = `${centerX}px`
      variable['--dropdown-transform-y'] = `${bottomY}px`
      break
    case 'top-start':
      variable['--dropdown-transform-x'] = `${startX}px`
      variable['--dropdown-transform-y'] = `${topY}px`
      break
    case 'top-end':
      variable['--dropdown-transform-x'] = `${endX}px`
      variable['--dropdown-transform-y'] = `${topY}px`
      break
    case 'top-center':
      variable['--dropdown-transform-x'] = `${centerX}px`
      variable['--dropdown-transform-y'] = `${topY}px`
      break
    default:
      break
  }
  return variable
})
const getActualPlacement = ({
  isOutside,
  directions
}: {
  isOutside: boolean
  directions: { top: boolean; right: boolean; bottom: boolean; left: boolean }
}) => {
  if (!isOutside) {
    return props.placement
  }
  let [pos, dir] = props.placement.split('-')
  pos = directions.top ? 'bottom' : pos
  pos = directions.bottom ? 'top' : pos
  if (directions.left || directions.right) {
    if (directions.left) {
      dir = 'start'
    } else if (directions.right) {
      dir = 'end'
    } else {
      dir = 'center'
    }
  }
  return `${pos}-${dir}`
}
const onOpen = () => {
  emit('open')
}
async function getCtxRect() {
  await nextTick()
  return {
    width: ctx.value?.offsetWidth || 0,
    height: ctx.value?.offsetHeight || 0
  }
}
watch(
  () => open.value,
  async (val) => {
    if (val) {
      !ctxRect.value && (ctxRect.value = await getCtxRect())
    }
  }
)
</script>
